import { useState, useRef, useCallback, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Mic, Square, Pause, Play, Trash2, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';

interface AudioRecorderProps {
  onRecordingComplete: (file: File) => void;
  maxDurationSeconds?: number;
  maxSizeMB?: number;
}

export const AudioRecorder = ({
  onRecordingComplete,
  maxDurationSeconds = 120,
  maxSizeMB = 10
}: AudioRecorderProps) => {
  const [isRecording, setIsRecording] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [duration, setDuration] = useState(0);
  const [audioUrl, setAudioUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [fileSize, setFileSize] = useState<number>(0);
  
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  // Format time as MM:SS
  const formatTime = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  // Format file size
  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  // Check browser support
  const isSupported = (): boolean => {
    return !!(navigator.mediaDevices && navigator.mediaDevices.getUserMedia && MediaRecorder);
  };

  // Start recording
  const startRecording = useCallback(async () => {
    if (!isSupported()) {
      setError('Audio recording is not supported in your browser. Please use Chrome, Firefox, or Safari.');
      return;
    }

    try {
      setError(null);
      const stream = await navigator.mediaDevices.getUserMedia({ 
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          sampleRate: 44100
        } 
      });
      
      streamRef.current = stream;
      chunksRef.current = [];

      // Determine best supported mimeType
      let mimeType = 'audio/webm';
      if (!MediaRecorder.isTypeSupported('audio/webm')) {
        mimeType = 'audio/ogg';
        if (!MediaRecorder.isTypeSupported('audio/ogg')) {
          mimeType = ''; // Let browser choose default
        }
      }

      const mediaRecorder = mimeType
        ? new MediaRecorder(stream, { mimeType })
        : new MediaRecorder(stream);

      mediaRecorderRef.current = mediaRecorder;

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          chunksRef.current.push(event.data);
        }
      };

      mediaRecorder.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: mediaRecorder.mimeType || 'audio/webm' });
        setFileSize(blob.size);
        
        // Check file size limit
        if (blob.size > maxSizeMB * 1024 * 1024) {
          setError(`Recording too large (${formatFileSize(blob.size)}). Maximum is ${maxSizeMB}MB.`);
          return;
        }

        const url = URL.createObjectURL(blob);
        setAudioUrl(url);
        
        // Convert to File
        const extension = mimeType.includes('ogg') ? 'ogg' : 'webm';
        const file = new File([blob], `audio-proof-${Date.now()}.${extension}`, {
          type: mimeType || 'audio/webm'
        });
        
        onRecordingComplete(file);
      };

      mediaRecorder.start(100); // Collect data every 100ms
      setIsRecording(true);
      setIsPaused(false);
      setDuration(0);

      // Start timer
      timerRef.current = setInterval(() => {
        setDuration(prev => {
          if (prev >= maxDurationSeconds) {
            stopRecording();
            toast.info('Maximum recording duration reached');
            return prev;
          }
          return prev + 1;
        });
      }, 1000);

    } catch (err: any) {
      if (err.name === 'NotAllowedError') {
        setError('Microphone permission denied. Please allow microphone access and try again.');
      } else if (err.name === 'NotFoundError') {
        setError('No microphone found. Please connect a microphone and try again.');
      } else {
        setError(`Failed to start recording: ${err.message}`);
      }
    }
  }, [maxDurationSeconds, maxSizeMB, onRecordingComplete]);

  // Stop recording
  const stopRecording = useCallback(() => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      mediaRecorderRef.current.stop();
    }
    
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }
    
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    
    setIsRecording(false);
    setIsPaused(false);
  }, []);

  // Pause/Resume recording
  const togglePause = useCallback(() => {
    if (!mediaRecorderRef.current) return;
    
    if (isPaused) {
      mediaRecorderRef.current.resume();
      setIsPaused(false);
      // Restart timer
      timerRef.current = setInterval(() => {
        setDuration(prev => {
          if (prev >= maxDurationSeconds) {
            stopRecording();
            return prev;
          }
          return prev + 1;
        });
      }, 1000);
    } else {
      mediaRecorderRef.current.pause();
      setIsPaused(true);
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    }
  }, [isPaused, maxDurationSeconds, stopRecording]);

  // Delete recording
  const deleteRecording = useCallback(() => {
    if (audioUrl) {
      URL.revokeObjectURL(audioUrl);
    }
    setAudioUrl(null);
    setDuration(0);
    setFileSize(0);
    setError(null);
  }, [audioUrl]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
      }
      if (audioUrl) URL.revokeObjectURL(audioUrl);
    };
  }, [audioUrl]);

  if (!isSupported()) {
    return (
      <div className="p-4 bg-red-50 dark:bg-red-950/20 border-2 border-red-300 dark:border-red-700 rounded-lg">
        <div className="flex items-start gap-3">
          <AlertCircle className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
          <div>
            <h4 className="font-bold text-red-900 dark:text-red-100 mb-1">Audio Recording Not Supported</h4>
            <p className="text-sm text-red-700 dark:text-red-300">
              Your browser doesn't support audio recording. Please use Chrome, Firefox, or Safari, or contact the bounty owner for alternative proof submission.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Recording Controls */}
      <div className="p-6 bg-gradient-to-br from-purple-50 to-indigo-50 dark:from-purple-950/20 dark:to-indigo-950/20 border-2 border-purple-300 dark:border-purple-700 rounded-xl">
        <div className="flex flex-col items-center gap-4">
          {/* Recording Indicator */}
          <div className="flex items-center gap-3">
            {isRecording && !isPaused && (
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 bg-red-500 rounded-full animate-pulse" />
                <span className="text-sm font-medium text-red-600 dark:text-red-400">Recording</span>
              </div>
            )}
            {isRecording && isPaused && (
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 bg-yellow-500 rounded-full" />
                <span className="text-sm font-medium text-yellow-600 dark:text-yellow-400">Paused</span>
              </div>
            )}
          </div>

          {/* Timer */}
          <div className="text-4xl font-mono font-bold text-purple-900 dark:text-purple-100">
            {formatTime(duration)}
          </div>
          <div className="text-xs text-muted-foreground">
            Max: {formatTime(maxDurationSeconds)}
          </div>

          {/* File Size (if recorded) */}
          {fileSize > 0 && (
            <div className="text-xs text-muted-foreground">
              Size: {formatFileSize(fileSize)} / {maxSizeMB}MB
            </div>
          )}

          {/* Control Buttons */}
          <div className="flex items-center gap-3">
            {!isRecording ? (
              <Button
                onClick={startRecording}
                size="lg"
                className="bg-red-500 hover:bg-red-600 text-white min-w-[120px]"
              >
                <Mic className="h-5 w-5 mr-2" />
                Start Recording
              </Button>
            ) : (
              <>
                <Button
                  onClick={togglePause}
                  variant="outline"
                  size="lg"
                  className="min-w-[100px]"
                >
                  {isPaused ? (
                    <>
                      <Play className="h-5 w-5 mr-2" />
                      Resume
                    </>
                  ) : (
                    <>
                      <Pause className="h-5 w-5 mr-2" />
                      Pause
                    </>
                  )}
                </Button>
                <Button
                  onClick={stopRecording}
                  variant="destructive"
                  size="lg"
                  className="min-w-[100px]"
                >
                  <Square className="h-5 w-5 mr-2" />
                  Stop
                </Button>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Audio Preview */}
      {audioUrl && (
        <div className="p-4 bg-white dark:bg-gray-900 border-2 border-green-300 dark:border-green-700 rounded-lg">
          <div className="flex items-center justify-between mb-3">
            <h4 className="font-bold text-green-900 dark:text-green-100">✓ Recording Complete</h4>
            <Button
              onClick={deleteRecording}
              variant="outline"
              size="sm"
              className="text-red-600 hover:bg-red-50 dark:hover:bg-red-950/30"
            >
              <Trash2 className="h-4 w-4 mr-1" />
              Delete
            </Button>
          </div>
          <audio
            ref={audioRef}
            src={audioUrl}
            controls
            className="w-full"
            aria-label="Audio proof preview"
          />
          <p className="text-xs text-muted-foreground mt-2">
            Listen to your recording before submitting
          </p>
        </div>
      )}

      {/* Error Message */}
      {error && (
        <div className="p-4 bg-red-50 dark:bg-red-950/20 border-2 border-red-300 dark:border-red-700 rounded-lg">
          <div className="flex items-start gap-3">
            <AlertCircle className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-medium text-red-900 dark:text-red-100">{error}</p>
              {error.includes('permission') && (
                <p className="text-xs text-red-700 dark:text-red-300 mt-2">
                  💡 <strong>How to enable:</strong> Click the lock/camera icon in your browser's address bar and allow microphone access.
                </p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Help Text */}
      {!audioUrl && !error && (
        <div className="text-xs text-muted-foreground space-y-1">
          <p>💡 Tips for a good recording:</p>
          <ul className="list-disc list-inside space-y-1 ml-2">
            <li>Speak clearly in a quiet environment</li>
            <li>Keep the microphone 6-12 inches from your mouth</li>
            <li>Avoid background noise and interruptions</li>
          </ul>
        </div>
      )}
    </div>
  );
};
